import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DeleteWorkOrderBulkRequest, RetriggerRequest, RetriggerStarSwatRequest } from '../models/bulk-workorder-actions.model';
import { utils } from '../helper/utils';
import { BehaviorSubject, Observable, catchError, map, of, timer } from 'rxjs';
import { AuditLog } from '../models/audit-log.model';
import { ShopRequest } from '../models/shop-request.model';
import { User } from '../models/user.model';
import { UserRequest } from '../models/user-request.model';
import { CodeDescription } from '../models/code-description.model';

@Injectable({
  providedIn: 'root',
})
export class AppSupportService {
  constructor(private httpClient: HttpClient) {}
  retriggerWorkOrders(retriggerRequest: RetriggerRequest): Observable<any> {
    return this.httpClient.post<any>(utils.GetApplicationSupportUrl('retrigger-job'), retriggerRequest);
  }

  retriggerStarSwatWorkOrders(request: RetriggerStarSwatRequest): Observable<any> {
    return this.httpClient.post<any>(utils.GetApplicationSupportUrl('retrigger-workorder'), request);
  }

  deleteWorkOrder(req: DeleteWorkOrderBulkRequest): Observable<any> {
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      body: req,
    };
    return this.httpClient.request<any>('DELETE', utils.GetApplicationSupportUrl('bulk-delete'), options);
  }

  updateShopsStatus(shopRequests: ShopRequest[]): Observable<any> {
    return this.httpClient.patch<any>(utils.GetApplicationSupportUrl('shops'), shopRequests);
  }

  getUserList(page: number, pageLimit: number, sort?: string, searchText?: string): Observable<HttpResponse<User[]>> {
    let url: string = `users?page=${page}&limit=${pageLimit}`;
    if (sort) {
      url += `&sort=${encodeURIComponent(sort)}`;
    }
    if (searchText) {
      url += `&searchText=${encodeURIComponent(searchText)}`;
    }
    return this.httpClient.get<User[]>(utils.GetApplicationSupportUrl(url), { observe: 'response' }).pipe(
      map((response: HttpResponse<User[]>) => {
        const body = response.body;
        if (body) {
          const mappedBody = body.map((item) => ({
            ...item,
            id: item.userId,
          }));

          return new HttpResponse({
            body: mappedBody,
            headers: response.headers,
          });
        } else {
          return response;
        }
      }),
    );
  }

  private roleList: BehaviorSubject<CodeDescription[]> = new BehaviorSubject<CodeDescription[]>([]);
  public getRoles(): Observable<CodeDescription[]> {
    if (this.roleList.value.length === 0) {
      return this.httpClient.get<CodeDescription[]>(utils.GetApplicationSupportUrl('roles')).pipe(
        map((roles: CodeDescription[]) => {
          this.roleList.next(roles);
          return roles;
        }),
      );
    } else {
      return this.roleList.asObservable();
    }
  }

  saveUser(userRequest: UserRequest): Observable<any> {
    return this.httpClient.post<any>(utils.GetApplicationSupportUrl('add-user'), userRequest);
  }

  updateUser(userRequest: UserRequest): Observable<any> {
    return this.httpClient.post<any>(utils.GetApplicationSupportUrl('update-user'), userRequest);
  }

  public getUserDetailsBasedOnUserName(userId: number): Observable<User> {
    return this.httpClient.get<User>(utils.GetApplicationSupportUrl(`user-details/${userId}`));
  }

  deleteUser(userId: number): Observable<any> {
    if (userId > 0) {
      return this.httpClient.delete<any>(`${utils.GetApplicationSupportUrl('delete-user')}/${userId}`);
    } else {
      return timer(2000);
    }
  }

  getUserAuditLogs(
    userId: string,
    isSystemLog: boolean = false,
    page: number = 1,
    limit: number = 100,
  ): Observable<HttpResponse<AuditLog[]>> {
    // Use Application Support API to fetch user audit history:
    // GET /api/users/{userId}/user-audit?isSystemLog={bool}&page={page}&limit={limit}
    const path = `users/${encodeURIComponent(userId)}/user-audit?isSystemLog=${isSystemLog}&page=${page}&limit=${limit}`;
    return this.httpClient.get<AuditLog[]>(utils.GetApplicationSupportUrl(path), { observe: 'response' }).pipe(
      catchError((err) => {
        console.error('Error fetching user audit logs:', err);
        return of(new HttpResponse<AuditLog[]>({ body: [], status: 200, statusText: 'OK' }));
      }),
    );
  }

  postMasterAuditLog(payload: {
    applicationId: number;
    moduleId: number;
    transactionCode: string;
    changeUser: string;
    changeUserId: number;
    masterDataMessage: any;
    previousMasterDataMessage: any;
  }): Observable<any> {
    // POST to audit log service: POST /master-audit-logs (moduleId lives in the request body)
    return this.httpClient.post<any>(utils.GetAuditLogUrl('master-audit-logs'), payload).pipe(
      catchError((err) => {
        console.error('Error posting master audit log:', err);
        return of(null);
      }),
    );
  }
}
